import { type Inspection, type Permit, type PermitGroup } from '@kalos/kalos-rpc';
import { format, isValid, parseISO, startOfDay } from 'date-fns';

import { NULL_TIME } from '../../constants';
import { inspectionResult } from './workspace';

export const NOT_LINKED_LABEL = 'Not linked';

// A permit group's job lives in its `po` field (a string). Empty, "0" or anything
// non-numeric means the group has no job yet.
export const getGroupJobNumber = (po?: string | null) => {
  const jobNumber = Number(po);
  return Number.isInteger(jobNumber) && jobNumber > 0 ? jobNumber : 0;
};

// Child permits created before phase 1 were stored with job_number 0. Those show
// their group's job instead; the stored value is left alone.
export const getEffectivePermitJobNumber = (jobNumber: number, groupPo?: string | null) =>
  jobNumber > 0 ? jobNumber : getGroupJobNumber(groupPo);

// Permits for a job: the ones stored with that job, plus active permits stored as 0
// under a group whose job it is. Deduplicated by id, direct matches first.
export const mergePermitsForJob = (
  directMatches: Permit[],
  groupsForJob: Pick<PermitGroup, 'permits'>[],
) => {
  const inherited = groupsForJob
    .flatMap((group) => group.permits)
    .filter((permit) => permit.isActive && permit.jobNumber === 0);
  const byId = new Map<number, Permit>();
  for (const permit of [...directMatches, ...inherited]) {
    if (!byId.has(permit.id)) byId.set(permit.id, permit);
  }
  return [...byId.values()];
};

// For display only. `po` is free text, so a legacy non-numeric value is shown as-is
// rather than hidden.
export const formatGroupJob = (po?: string | null) => {
  const value = (po ?? '').trim();
  return value && value !== '0' ? value : NOT_LINKED_LABEL;
};

export const formatJobNumber = (jobNumber: number) =>
  jobNumber > 0 ? jobNumber.toString() : NOT_LINKED_LABEL;

// Inspection dates are NOT NULL in the database and default to 0001-01-01, which
// means "no date entered".
export const INSPECTION_EMPTY_DATE = NULL_TIME;

// Design system: dates read as human dates (Jul 27, 2026), never ISO, and an empty
// value is an em dash.
const HUMAN_DATE = 'MMM d, yyyy';
export const EMPTY_VALUE = '—';

// Date-only pickers: the shared DatePicker otherwise appends "00:00".
export const DATE_ONLY_DISPLAY_FORMAT = { hour24: HUMAN_DATE, hour12: HUMAN_DATE };

export const parseInspectionDate = (date?: string | null) => {
  // Empty dates come back as Go's zero time, in either format ("0001-01-01 00:00:00" or
  // "0001-01-01T00:00:00Z").
  if (!date || date === NULL_TIME || date.startsWith('0000-00-00') || date.startsWith('0001-01-01'))
    return undefined;
  const parsed = parseISO(date);
  return isValid(parsed) ? parsed : undefined;
};

/**
 * A server timestamp (created_at, updated_at, status_changed_at): the database runs in
 * UTC and sends "2026-11-01 15:21:07" with no zone, so it's read as UTC and shown local.
 */
export const parseServerTimestamp = (value?: string | null) => {
  if (!value || value.startsWith('0000-00-00') || value.startsWith('0001-01-01')) return undefined;
  const iso = value.trim().replace(' ', 'T');
  const parsed = new Date(/(Z|[+-]\d\d:?\d\d)$/.test(iso) ? iso : `${iso}Z`);
  return isValid(parsed) ? parsed : undefined;
};

export const formatInspectionDate = (date?: string | null) => {
  const parsed = parseInspectionDate(date);
  return parsed ? format(parsed, HUMAN_DATE) : EMPTY_VALUE;
};

// A confirmed inspection booking includes its time when one was set ("Oct 2, 2026,
// 9:30 AM"); a requested date is a date only. Empty reads "—".
export const formatConfirmedDate = (date?: string | null) => {
  const parsed = parseInspectionDate(date);
  if (!parsed) return EMPTY_VALUE;
  const hasTime = parsed.getHours() !== 0 || parsed.getMinutes() !== 0;
  return format(parsed, hasTime ? `${HUMAN_DATE}, h:mm a` : HUMAN_DATE);
};

// The permit's key date on the permit request page: its next inspection from today on.
// A confirmed booking (scheduled date) and a requested date read differently, since a
// requested date is never a booking (docs/modules/permitting-workspace.md).
export const nextInspectionLabel = (inspections: Inspection[], now = new Date()) => {
  const today = startOfDay(now);
  const upcoming = inspections
    .map((inspection) => {
      const confirmed = parseInspectionDate(inspection.scheduledDate);
      if (confirmed) return confirmed >= today ? { date: confirmed, confirmed: true } : undefined;
      const requested = parseInspectionDate(inspection.requestorScheduledDate);
      return requested && requested >= today ? { date: requested, confirmed: false } : undefined;
    })
    .filter((next): next is { date: Date; confirmed: boolean } => !!next)
    .sort((a, b) => a.date.getTime() - b.date.getTime())[0];

  if (!upcoming) return undefined;
  if (!upcoming.confirmed) return `Inspection requested for ${format(upcoming.date, HUMAN_DATE)}`;
  return `Inspection confirmed ${formatConfirmedDate(format(upcoming.date, 'yyyy-MM-dd HH:mm:ss'))}`;
};

// One line for a start and end date: "Aug 25 to Aug 25, 2025", with the year once when
// both dates share it. A missing end shows as "—".
export const formatDateRange = (start?: string | null, end?: string | null) => {
  const from = parseInspectionDate(start);
  const to = parseInspectionDate(end);
  if (!from && !to) return EMPTY_VALUE;
  if (!from || !to) return `${formatInspectionDate(start)} to ${formatInspectionDate(end)}`;
  // A one-day range reads as that one date.
  if (from.toDateString() === to.toDateString()) return format(from, HUMAN_DATE);
  const sameYear = from.getFullYear() === to.getFullYear();
  return `${format(from, sameYear ? 'MMM d' : HUMAN_DATE)} to ${format(to, HUMAN_DATE)}`;
};

// Same format as timestamp(), which falls back to now when given no date.
export const toInspectionDateValue = (date?: Date) =>
  date ? format(date, 'yyyy-MM-dd HH:mm:ss') : INSPECTION_EMPTY_DATE;

// docs/modules/permitting-workspace.md: a request is titled by the first line of its Scope of
// Work. Without one it's named by what is known of its job, "Job # 256429 · Elite
// Rental" (either part alone when only one is known), else "Untitled request".
export const UNTITLED_REQUEST = 'Untitled request';
export const groupTitle = (
  scopeOfWork?: string | null,
  fallback: { po?: string | null; customer?: string } = {},
) => {
  const firstLine = (scopeOfWork ?? '').trim().split('\n')[0]?.trim();
  if (firstLine) return firstLine;
  const job = formatGroupJob(fallback.po);
  return (
    [job === NOT_LINKED_LABEL ? '' : `Job # ${job}`, fallback.customer?.trim()]
      .filter(Boolean)
      .join(' · ') || UNTITLED_REQUEST
  );
};

export const groupJobLine = (po?: string | null) => {
  const job = formatGroupJob(po);
  return job === NOT_LINKED_LABEL ? NOT_LINKED_LABEL : `Job # ${job}`;
};

// Person pickers show the name only, never "(ID: …)" (86bc85juw round 2).
export const personLabel = (user: { firstname: string; lastname: string }) =>
  `${user.firstname} ${user.lastname}`.trim();

// Notes as saved can carry indentation from older requests (a template literal indented
// every line). Display strips each line's leading whitespace and blank lines at either
// end, and keeps the line breaks. The stored text is never changed by this.
export const cleanNotes = (notes?: string | null) =>
  (notes ?? '')
    .split('\n')
    .map((line) => line.trimStart())
    .join('\n')
    .trim();

// Where an inspection is next: pending with no booking, confirmed (pending with a
// booking), passed, failed or cancelled, or with a private provider (submitted to the
// provider, or pending a correction) until its result comes back.
export type InspectionStep =
  | 'pending'
  | 'confirmed'
  | 'passed'
  | 'failed'
  | 'cancelled'
  | 'with provider';
export const inspectionStep = (inspection: Inspection, statusCode: string): InspectionStep => {
  const result = inspectionResult(statusCode);
  if (result === 'passed' || result === 'cancelled') return result;
  // Partial and No access end the attempt without passing, like Failed.
  if (result === 'failed' || result === 'partial' || result === 'no access') return 'failed';
  if (result === 'submitted to provider' || result === 'pending correction') {
    return 'with provider';
  }
  return isConfirmed(inspection) ? 'confirmed' : 'pending';
};

/**
 * A permit's next inspection for a card: the soonest from today on, "Oct 2 · Confirmed"
 * (a booking with its number) or "Oct 4 · Requested". Undefined when none is ahead.
 */
export const nextInspectionShort = (inspections: Inspection[], now = new Date()) => {
  const today = startOfDay(now);
  const next = inspections
    .map((inspection) => {
      const confirmed = isConfirmed(inspection);
      const date = confirmed
        ? parseInspectionDate(inspection.scheduledDate)
        : parseInspectionDate(inspection.requestorScheduledDate) ??
          parseInspectionDate(inspection.scheduledDate);
      return date && date >= today ? { date, confirmed } : undefined;
    })
    .filter((item): item is { date: Date; confirmed: boolean } => !!item)
    .sort((a, b) => a.date.getTime() - b.date.getTime())[0];
  return next
    ? `${format(next.date, 'MMM d')} · ${next.confirmed ? 'Confirmed' : 'Requested'}`
    : undefined;
};

/** Booked (a confirmed date and time) and has its Confirmation #: only then Confirmed. */
export const isConfirmed = (inspection: Inspection) =>
  !!parseInspectionDate(inspection.scheduledDate) && !!(inspection.confirmationNumber ?? '').trim();

// "County, In person · Requested Sep 26 · Not confirmed": the row's meta line, leaving out
// what is empty. Path and method read as their list names.
export const inspectionMeta = (inspection: Inspection, statusCode: string) => {
  const requested = parseInspectionDate(inspection.requestorScheduledDate);
  const step = inspectionStep(inspection, statusCode);
  const how = [inspection.inspectionPathName.trim(), inspection.inspectionMethodName.trim()]
    .filter(Boolean)
    .join(', ');
  return [
    how || undefined,
    requested ? `Requested ${format(requested, 'MMM d')}` : undefined,
    isConfirmed(inspection)
      ? `Confirmed ${formatConfirmedDate(inspection.scheduledDate)} · #${inspection.confirmationNumber.trim()}`
      : parseInspectionDate(inspection.scheduledDate)
        ? // Booked before Confirmation # existed: it still needs one to read Confirmed.
          `Booked ${formatConfirmedDate(inspection.scheduledDate)} · No confirmation #`
        : step === 'pending'
          ? 'Not confirmed'
          : undefined,
  ]
    .filter(Boolean)
    .join(' · ');
};
